import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useBrand } from '../brand';
import { useBuyOffer } from './useBuyOffer';
import { accessEndsLabel, offerPrice, saleEndsLabel } from './offer-format';
import './checkout.css';

/**
 * A direct checkout link (/buy/:packageCode), for emails and ads that skip the pricing page.
 * Signed in, it goes straight to Stripe Checkout. Signed out, it shows the offer and sends
 * them to create an account (or sign in), then CheckoutIntentResumer brings them back here
 * and on to Stripe. A closed or unknown offer says so instead of failing at checkout.
 * The plain look: a campaign can dress its own page around useBuyOffer.
 */
export function BuyView() {
  const brand = useBrand();
  const { packageCode = '' } = useParams();
  const { state, offer, retry, goToAuth } = useBuyOffer(packageCode);

  if (state === 'loading') return <Panel title="One moment…" />;

  if (state === 'missing' || !offer) {
    return (
      <Panel title="This offer isn't available">
        <p className="ui-buy-text">The link may be out of date. Have a look at what {brand.name} has on offer now.</p>
        <div className="ui-buy-actions"><Link className="ui-buy-btn primary" to="/pricing">See plans</Link></div>
      </Panel>
    );
  }

  if (state === 'closed') {
    return (
      <Panel title={`Sales of the ${offer.name} have closed`}>
        <p className="ui-buy-text">Thanks for your interest. You can still join {brand.name} on one of our current plans.</p>
        <div className="ui-buy-actions"><Link className="ui-buy-btn primary" to="/pricing">See plans</Link></div>
      </Panel>
    );
  }

  const until = accessEndsLabel(offer);
  const closes = saleEndsLabel(offer);
  const summary = (
    <>
      <p className="ui-buy-price">{offerPrice(offer)}</p>
      {offer.description && <p className="ui-buy-text">{offer.description}</p>}
      {until && <p className="ui-buy-detail">{until}</p>}
      {closes && <p className="ui-buy-detail">{closes}</p>}
    </>
  );

  if (state === 'checkout' || state === 'error') {
    return (
      <Panel title={offer.name}>
        {summary}
        {state === 'error' ? (
          <>
            <p className="ui-buy-error" role="alert">We couldn't open the secure checkout. Please try again.</p>
            <div className="ui-buy-actions">
              <button type="button" className="ui-buy-btn primary" onClick={retry}>Try again</button>
            </div>
          </>
        ) : (
          <p className="ui-buy-text" aria-live="polite">Taking you to secure checkout…</p>
        )}
      </Panel>
    );
  }

  return (
    <Panel title={offer.name}>
      {summary}
      <p className="ui-buy-text">Create your free {brand.name} account and you'll go straight on to secure checkout.</p>
      <div className="ui-buy-actions">
        <button type="button" className="ui-buy-btn primary" onClick={() => goToAuth('/register')}>Create account and continue</button>
        <button type="button" className="ui-buy-btn" onClick={() => goToAuth('/login')}>I already have an account</button>
      </div>
    </Panel>
  );
}

function Panel({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <section className="ui-buy" aria-labelledby="ui-buy-title">
      <h1 id="ui-buy-title" className="ui-buy-title">{title}</h1>
      {children}
    </section>
  );
}
